import {useEffect, useRef, useState} from "react";

const DHW_BLUE = "#001489";
const SUPPORT_PHONE_DISPLAY = "1-866-777-3850";
const SUPPORT_PHONE_TEL = "+18667773850";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const errorMessage = (dobError) => {
  if (dobError === "ambiguous") {
    return "More than one person on this account shares that date of birth. Please call the Case Management Services Line for help.";
  }
  if (dobError === "no_match") {
    return "That date of birth didn't match. Check the date and try again, or call the Case Management Services Line if you need help.";
  }
  if (dobError === "request_error") {
    return "We couldn't check that right now. Please try again in a moment, or call the Case Management Services Line if it keeps happening.";
  }
  return null;
};

// Non-dismissible: shared phone/email must prove which participant they are
// before any case/orientation UI is usable. No cancel, no backdrop click,
// no Escape — refresh without a stored DOB returns needs_dob again.
const DobDisambiguationModal = ({
  open,
  onSubmit,
  dobError,
  submitting = false,
}) => {
  const [value, setValue] = useState("");
  const dialogRef = useRef(null);
  const inputRef = useRef(null);
  const previousFocusRef = useRef(null);
  const alertText = errorMessage(dobError);

  useEffect(() => {
    if (!open) return undefined;
    const dialog = dialogRef.current;
    previousFocusRef.current = document.activeElement;

    try {
      if (dialog && typeof dialog.showModal === "function" && !dialog.open) {
        dialog.showModal();
      }
    } catch {
      // Already open, or a test environment without dialog top-layer support.
    }
    inputRef.current?.focus();

    const onCancel = (e) => e.preventDefault();
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.key !== "Tab" || !dialog) return;
      const list = Array.from(dialog.querySelectorAll(FOCUSABLE));
      if (list.length === 0) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    dialog?.addEventListener("cancel", onCancel);
    dialog?.addEventListener("keydown", onKeyDown);

    return () => {
      dialog?.removeEventListener("cancel", onCancel);
      dialog?.removeEventListener("keydown", onKeyDown);
      try {
        if (dialog && typeof dialog.close === "function" && dialog.open) {
          dialog.close();
        }
      } catch {
        // jsdom / already closed
      }
      const prev = previousFocusRef.current;
      if (prev && typeof prev.focus === "function") prev.focus();
    };
  }, [open]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!value || submitting) return;
    onSubmit(value);
  };

  return (
    <dialog
      ref={dialogRef}
      className="w-full max-w-md rounded-2xl border-0 bg-white p-6 shadow-xl backdrop:bg-slate-900/50"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="dob-modal-title"
      aria-describedby="dob-modal-desc"
      data-testid="dob-disambiguation-modal"
    >
      <h2 id="dob-modal-title" className="text-xl font-bold text-[#191919]">
        Confirm your date of birth
      </h2>
      <p id="dob-modal-desc" className="mt-2 text-sm text-slate-600">
        More than one person uses this phone or email. Enter your date of birth
        so we can show the right work plan.
      </p>

      {alertText && (
        <div
          role="alert"
          className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
          data-testid="dob-error-alert"
        >
          {alertText}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-5">
        <label
          htmlFor="dob-input"
          className="block text-sm font-semibold text-[#191919]"
        >
          Date of birth
        </label>
        <input
          ref={inputRef}
          id="dob-input"
          type="date"
          required
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-base text-[#191919] focus:border-[#001489] focus:outline-none focus:ring-2 focus:ring-[#001489]/30"
          data-testid="dob-input"
        />
        <button
          type="submit"
          disabled={!value || submitting}
          className="mt-5 w-full rounded-xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
          style={{backgroundColor: DHW_BLUE}}
          data-testid="dob-submit"
        >
          {submitting ? "Checking…" : "Continue"}
        </button>
      </form>

      <p className="mt-5 text-sm text-slate-600" data-testid="dob-help-phone">
        Having trouble? Call the Case Management Services Line at{" "}
        <a
          href={`tel:${SUPPORT_PHONE_TEL}`}
          className="font-semibold underline"
          style={{color: DHW_BLUE}}
        >
          {SUPPORT_PHONE_DISPLAY}
        </a>{" "}
        (Monday–Friday, 8:00 a.m.–6:00 p.m. MT).
      </p>
    </dialog>
  );
};

export default DobDisambiguationModal;
